import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  EuiBottomBar,
  EuiButtonIcon,
  EuiCollapsibleNav,
  EuiCollapsibleNavGroup,
  EuiFlexGroup,
  EuiFlexItem,
  EuiListGroup,
  EuiPage,
  EuiPageBody,
  EuiSpacer,
  EuiTextAlign,
} from "@elastic/eui";
import { ErrorBoundary } from "react-error-boundary";
import ErrorFallback from "../ErrorFallback/ErrorFallback";
import { COLORS, COPYRIGHT_MESSAGE } from "Consts";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { authSelector } from "reduxStore/auth/auth.selectors";
import { checkRouteExceptions, isSubjectAuthorizedForUser } from "utils";
import MyProfile from "pages/_rest/MyProfile";
import Unauthorized from "pages/ServicePages/Unauthorized";
import Emitter from "services/UtilsService/Emitter";
import { defaultNotSelectedClient, nonSideMenuItems } from "./PageConsts";
import { getActivePermissions, createNavItem, checkFilterResetRule } from "./PageUtils";
import { TopSection } from "./components/TopSection";
import { ClientSelect } from "./components/ClientSelector";
import { MenuSelector } from "./components/MenuSelector";
import { menuPages, finOpsMenuPages } from "settings/menu";
import { REDIRECT_TO_MAIN_PAGE } from "settings/menu/dashboardsPages";
import "styles/global/colors.css";
import "styles/global/fonts.css";
import "styles/global/page.css";

const PageComponent = ({ children }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const prevLocation = useRef(location.pathname);
  const ref = useRef<HTMLDivElement>(null);
  const [isFinOpsMenu, setIsFinOpsMenu] = useState<boolean>(false);
  const [sectionTitle, setSectionTitle] = useState<any>({});
  const [isForbidden, setIsForbidden] = useState<boolean>(false);
  const [isMyProfileVisible, setIsMyProfileVisible] = useState<boolean>(false);
  const [openedMenuItem, setOpenedMenuItem] = useState({ id: "Dashboards", isOpen: true });
  // const { currentUser, permissions } = useSelector(authSelector, shallowEqual);
  const { clientId, clientInfo, navIsOpen, GlobalTopFilterObject } = useSelector(
    globalFilterSelector,
    shallowEqual
  );

  const MENU_ITEMS = isFinOpsMenu ? finOpsMenuPages : menuPages;
  const isExceptionPage = checkRouteExceptions();
  const filterTarget: any = document.getElementById("filter_box");
  const filterTargetHeight = filterTarget?.offsetHeight || 42;
  const isOnboardingSkipped =
    localStorage.getItem(`skip_onboarding`) === `${clientId}` || clientInfo?.isOnboardingComplete;

  const handleMenuSelect = (v: boolean) => {
    setIsFinOpsMenu(v);
    if (v === true) {
      setOpenedMenuItem({ id: "FinOps", isOpen: true });
    } else {
      setOpenedMenuItem({ id: "Dashboards", isOpen: true });
    }
  };

  const getMenuItem = (path: string) => {
    const _path = path?.includes("home") ? "/dashboards/dba" : path;
    let activeSectionItem: any = {};
    let activeTopNavItem: any = {};
    let activeMenuItem: any = {};
    const hasSideMenuActiveItem = MENU_ITEMS.some((rootItem: any) => {
      return (rootItem.items || []).some((childItem: any) => {
        activeTopNavItem = childItem.topNavLinks?.find(
          (topNavItem: any) => _path === topNavItem.path
        );
        const isActive = _path === childItem.path || activeTopNavItem; // navigation object that matches url

        if (isActive) {
          activeSectionItem = rootItem;
          activeMenuItem = childItem;
        }
        return isActive;
      });
    });

    const hasNonSideMenuActiveItem = !hasSideMenuActiveItem
      ? nonSideMenuItems.some((menuItem: any) => {
          activeTopNavItem = menuItem.topNavLinks?.find((topNavItem: any) => {
            if (topNavItem.subItems) {
              const hasActiveSubItem = topNavItem.subItems.some(
                (subItem: any) => _path === subItem.path
              );
              return hasActiveSubItem;
            } else {
              return _path === topNavItem.path;
            }
          });

          if (activeTopNavItem?.subItems) {
            activeTopNavItem = {
              ...activeTopNavItem,
              ...activeTopNavItem.subItems?.find((subItem: any) => _path === subItem.path),
            };
          }

          const isActive = _path === menuItem.path || activeTopNavItem; // navigation object that matches url

          if (isActive) {
            activeSectionItem = menuItem?.parent || null;
            activeMenuItem = menuItem;
          }
          return isActive;
        })
      : false;

    return hasSideMenuActiveItem || hasNonSideMenuActiveItem
      ? {
          activeSectionItem,
          activeMenuItem,
          activeTopNavItem,
        }
      : null;
  };

  const currentMenuItem = getMenuItem(location.pathname);

  const IsMenuItemAuthorized = ({ permissions }: any) => {
    return permissions === undefined || isSubjectAuthorizedForUser(permissions);
  };

  const getDefaultPathItem = (activeItem: any) => {
    const activePermissions = getActivePermissions(activeItem);
    const isDefaultNavForbidden = !IsMenuItemAuthorized({
      permissions: activePermissions,
    });
    return !isDefaultNavForbidden
      ? (activeItem?.activeTopNavItem ?? activeItem.activeMenuItem)
      : activeItem?.activeMenuItem.topNavLinks.find((t: any) =>
          IsMenuItemAuthorized({
            permissions: t.permissions,
          })
        );
  };

  const getInitCategoryOpen = (id: any) => {
    return id === "Dashboards" || id === currentMenuItem?.activeSectionItem?.id;
  };

  const setIsCategoryOpen = (id: any, isOpen: any) => {
    setOpenedMenuItem({ id, isOpen });
  };

  const initNavMenuItems = () => {
    const _menu: any[] = [];

    MENU_ITEMS.forEach((menuSectionObj: any) => {
      const menuSubItems: any[] = [];
      menuSectionObj.items.forEach((subSectionItem: any) => {
        menuSubItems.push(createNavItem({ ...subSectionItem, clientInfo, IsMenuItemAuthorized }));
      });
      const menuSectionNavItem: any = createNavItem({
        ...menuSectionObj,
        items: menuSubItems,
        clientInfo,
        IsMenuItemAuthorized,
      });
      _menu.push(menuSectionNavItem);
    });

    let cleanItems = _menu.filter((rootItem) => !!rootItem);
    cleanItems = cleanItems.filter((rootItem) => {
      rootItem.items = rootItem.items.filter(
        (subItem: any) =>
          !!subItem &&
          (subItem.topNavLinks === undefined ||
            (subItem.topNavLinks || []).some((t: any) => IsMenuItemAuthorized(t)))
      );
      return rootItem.items.length > 0;
    });

    return cleanItems;
  };

  const sideNavItems = isOnboardingSkipped ? initNavMenuItems() : [];

  const redirect = (path: string) => {
    if (path) {
      navigate(path);
      //@ts-ignore
      Emitter.emit("pageUnload");
    }
  };

  if (navIsOpen) {
    //new state is opened?
    if (!document.body.classList.contains("side-nav-open")) {
      document.body.classList.add("side-nav-open");
    }
  } else if (!navIsOpen) {
    // new state is closed
    if (document.body.classList.contains("side-nav-open")) {
      document.body.classList.remove("side-nav-open");
    }
  }

  const updateSectionTitle = (activeSectionItem: any) => {
    setSectionTitle({ label: activeSectionItem?.label, icon: activeSectionItem?.icon });
  };

  const toggleSideNav = () => {
    dispatch({
      type: globalFilterTypes.TOGGLE_SIDE_NAV,
    });

    window.dispatchEvent(new Event("resize")); //otherwise the charts are not being resized
  };

  useEffect(() => {
    setOpenedMenuItem({
      id:
        currentMenuItem?.activeSectionItem?.id === "Dashboards"
          ? "Dashboards"
          : currentMenuItem?.activeSectionItem?.id,
      isOpen: true,
    });
    updateSectionTitle(currentMenuItem?.activeSectionItem);
  }, []);

  useEffect(() => {
    dispatch({
      type: globalFilterTypes.SET_FILTER_MODE,
      payload: "global",
    });

    localStorage.setItem("lastUrl", prevLocation.current);
    prevLocation.current = location.pathname;

    if (openedMenuItem?.id !== currentMenuItem?.activeSectionItem?.id) {
      setOpenedMenuItem({ id: currentMenuItem?.activeSectionItem?.id, isOpen: true });
    }

    if (sectionTitle?.label !== currentMenuItem?.activeMenuItem?.label) {
      updateSectionTitle(currentMenuItem?.activeSectionItem);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (currentMenuItem) {
      const permissions = getActivePermissions(currentMenuItem);
      const isForbidden = !IsMenuItemAuthorized({ permissions });
      setIsForbidden(isForbidden);
    }
  }, [IsMenuItemAuthorized, location.pathname]);

  useEffect(() => {
    if (window) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [window.location.pathname]);

  return (
    <>
      <TopSection
        currentMenuItem={currentMenuItem}
        sectionTitle={sectionTitle}
        setIsMyProfileVisible={setIsMyProfileVisible}
        navIsOpen={navIsOpen}
        toggleSideNav={toggleSideNav}
        IsMenuItemAuthorized={IsMenuItemAuthorized}
        updateSectionTitle={updateSectionTitle}
      />
      <EuiPage>
        <EuiCollapsibleNav
          aria-label="Main navigation"
          dockedBreakpoint="l"
          hideCloseButton
          id="MainNav"
          className="mainNav"
          isDocked={navIsOpen}
          isOpen={navIsOpen}
          maxWidth={250}
          onClose={toggleSideNav}
          outsideClickCloses
          ownFocus={false} // docking will work above "l" screen size
          showButtonIfDocked={false}>
          <>
            <div className={"side-menu-top "}>
              <EuiFlexGroup direction="column" gutterSize="xs">
                <EuiFlexItem>
                  <EuiFlexGroup
                    alignItems="flexStart"
                    justifyContent="spaceBetween"
                    responsive={false}>
                    <EuiFlexItem
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        navigate(REDIRECT_TO_MAIN_PAGE);
                      }}>
                      <img
                        alt="Fortified Data"
                        //@ts-ignore
                        fetchpriority="high"
                        className={"main_menu_logo_new"}
                        src={"/ui/images/logos/wisdom_logo_new.svg"}
                      />
                    </EuiFlexItem>
                    <EuiFlexItem grow={false}>
                      <EuiButtonIcon
                        style={{ marginRight: "-4px", marginTop: "5px", width: "40px" }}
                        aria-label="Toggle main navigation"
                        iconSize="m"
                        iconType="menuLeft"
                        onClick={toggleSideNav}
                        size="s"
                      />
                    </EuiFlexItem>
                  </EuiFlexGroup>
                </EuiFlexItem>
                <EuiSpacer size="m" />
                <EuiFlexItem style={{ marginLeft: "6px" }}>
                  {navIsOpen && (
                    <ClientSelect
                      ClassName="clients-select-side-menu"
                      IsDisabled={currentMenuItem?.activeMenuItem?.requireClientSelection === false}
                      LinkColor={COLORS.$medGrey}
                      ShowAvatar
                    />
                  )}
                </EuiFlexItem>
                {/* !!! we are going to replace this concept !!! */}
                {/* <EuiFlexItem style={{ marginLeft: "6px" }}>
                  <MenuSelector
                    disabled={currentMenuItem?.activeMenuItem?.requireClientSelection === false}
                    setIsFinOpsMenu={handleMenuSelect}
                  />
                  <EuiSpacer />
                </EuiFlexItem> */}
              </EuiFlexGroup>
            </div>
            {/* with menu selector marginTop: "45px" , without: 22px */}
            <div className="eui-yScroll" style={{ marginTop: "22px" }}>
              {[...sideNavItems]
                .filter((sn) => !!sn)
                .map((rootItem: any, idx: number) => {
                  return (
                    <div key={`menu_item_${idx}`}>
                      {rootItem.label === "Performance" && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "16px",
                            paddingTop: "26px",
                            paddingBottom: "16px",
                            paddingRight: "12px",
                          }}>
                          <div
                            style={{
                              marginRight: "6px",
                              fontFamily: "sans-serif",
                              fontWeight: 600,
                              fontSize: "var(--fs10)",
                              letterSpacing: "2px",
                              textTransform: "uppercase",
                              color: "var(--menu-category)",
                            }}>
                            MONITORING
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: "1px",
                              //border: "0.5px solid #ffffff33",
                            }}
                          />
                        </div>
                      )}

                      {rootItem.label === "Configuration" && ( //"Admin Console"
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            paddingLeft: "16px",
                            paddingTop: "26px",
                            paddingBottom: "16px",
                            paddingRight: "12px",
                          }}>
                          <div
                            style={{
                              marginRight: "6px",
                              fontFamily: "sans-serif",
                              fontWeight: 600,
                              fontSize: "var(--fs10)",
                              letterSpacing: "2px",
                              textTransform: "uppercase",
                              color: "var(--menu-category)",
                            }}>
                            Admin
                          </div>
                          <div
                            style={{
                              width: "100%",
                              height: "1px",
                              //border: "0.5px solid #ffffff33",
                            }}
                          />
                        </div>
                      )}
                      {/* @ts-ignore */}
                      <EuiCollapsibleNavGroup
                        style={{ overflow: "visible" }}
                        className={rootItem.isSingle ? "is_single" : "not_single"}
                        paddingSize="s"
                        iconSize="s"
                        iconType={rootItem.icon}
                        initialIsOpen={getInitCategoryOpen(rootItem.id)}
                        isCollapsible={!rootItem.isSingle}
                        id={rootItem.id}
                        key={rootItem.id}
                        onToggle={(isCategoryOpen) => {
                          if (!rootItem.isSingle) setIsCategoryOpen(rootItem.id, isCategoryOpen);
                        }}
                        forceState={
                          openedMenuItem.id === rootItem.id && openedMenuItem.isOpen
                            ? "open"
                            : "closed"
                        }
                        onClick={() => {
                          if (rootItem.isSingle) {
                            checkFilterResetRule(dispatch, GlobalTopFilterObject);
                            const activeItem: any = getMenuItem(rootItem.items[0].path);
                            updateSectionTitle(activeItem.activeSectionItem);
                            const defaultNav = getDefaultPathItem(activeItem);
                            return redirect(defaultNav.path);
                          }
                        }}
                        title={
                          rootItem.isSingle ? (
                            <div
                              style={{
                                padding: "3px 8px",
                                cursor: "pointer",
                                marginLeft: "-8px",
                                fontWeight: 400,
                              }}
                              className={
                                window.location.pathname === rootItem.items[0].path
                                  ? "activeMenuItem"
                                  : ""
                              }>
                              <span>{rootItem.label}</span>
                            </div>
                          ) : (
                            <span
                              style={{
                                fontWeight: 400,
                              }}>
                              {rootItem.label}
                            </span>
                          )
                        }
                        titleSize="xxs"
                        css={{
                          padding: rootItem.isSingle ? "6px 18px" : "10px 18px",
                        }}>
                        {!rootItem.isSingle && (
                          <EuiListGroup
                            color="text"
                            gutterSize="s"
                            style={{ paddingRight: 0 }}
                            listItems={(rootItem.items || [])?.map((childItem: any) => {
                              const activeItem =
                                window.location.pathname === childItem.path
                                  ? childItem
                                  : childItem.topNavLinks &&
                                    childItem.topNavLinks.find(
                                      (topNavItem: any) =>
                                        window.location.pathname === topNavItem.path
                                    ); // navigation object that matches url

                              return {
                                label: childItem.label,
                                "data-test-subj": childItem.path,
                                onClick: () => {
                                  checkFilterResetRule(dispatch, GlobalTopFilterObject);
                                  const activeItem: any = getMenuItem(childItem.path);
                                  updateSectionTitle(activeItem.activeSectionItem);
                                  const defaultNav = getDefaultPathItem(activeItem);
                                  return redirect(defaultNav.path);
                                },
                                size: "s",
                                isActive: !!activeItem,
                                className: activeItem ? "activeMenuItem menuItem" : "menuItem",
                              };
                            })}
                          />
                        )}
                      </EuiCollapsibleNavGroup>
                    </div>
                  );
                })}
              {/* Docking button only for larger screens that can support it */}
            </div>
          </>
        </EuiCollapsibleNav>

        <EuiPageBody
          id="page_body"
          className={`page-body`}
          component="div"
          style={{
            marginTop: isExceptionPage ? "0px" : `${filterTargetHeight}px`,
          }}>
          <div
            className="page-content"
            style={
              currentMenuItem?.activeTopNavItem?.label !== "Compare"
                ? {
                    padding: "12px 20px",
                    paddingBottom: "40px",
                  }
                : {
                    padding: "0 20px",
                    margin: "0px",
                  }
            }>
            {!isForbidden && (
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <div ref={ref}>{children}</div>
              </ErrorBoundary>
            )}
            {isForbidden && <Unauthorized />}
          </div>

          <footer>
            <EuiBottomBar
              className="bottom-bar"
              paddingSize="s"
              position="fixed"
              style={{ background: "var(--app-background)", opacity: 0.8 }}>
              <EuiTextAlign textAlign="center">
                <small>{COPYRIGHT_MESSAGE}</small>
              </EuiTextAlign>
            </EuiBottomBar>
          </footer>

          {isMyProfileVisible && (
            <MyProfile
              onClose={() => {
                setIsMyProfileVisible(false);
              }}
            />
          )}
        </EuiPageBody>
      </EuiPage>
    </>
  );
};

export default PageComponent;
